<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { RouterLink, useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { isAiJobSettled, type ActiveAiTest } from '@kitobxonlik/contracts';
import AppIcon from '@/components/ui/AppIcon.vue';
import { useAiTestsStore } from '@/stores/aiTests.store';
import { aiJobStatusKey, aiJobTone, isAiJobFailed, isAiJobReceived } from '@/utils/aiJob';

/**
 * AI jarayonlari — har sahifada pastki o'ng burchakda (2026-10-30). Kitob
 * AI'ga yuborilgach admin boshqa sahifaga o'tsa ham bosqich, foiz va natija
 * ko'rinib turadi; kitob nomi — uning savollari sahifasiga havola.
 *
 * Shu kitobning savollari sahifasida qator ko'rsatilmaydi — u yerda
 * `AiTestsPanel` bor; o'sha yerda tugagan natija yopilgan hisoblanadi.
 */
const { t, te } = useI18n();
const route = useRoute();
const store = useAiTestsStore();

/** Progress chizig'idagi to'ldirilgan qismning eng kichik kengligi, % */
const MIN_BAR = 4;

const collapsed = ref(false);

/** Ochiq turgan savollar sahifasining kitobi */
const openBookId = computed(() =>
  route.name === 'admin-questions' ? Number(route.params.id) : null,
);
const visible = computed(() => store.items.filter((item) => item.job.book_id !== openBookId.value));
const runningCount = computed(
  () => store.running.filter((item) => item.job.book_id !== openBookId.value).length,
);

watch(
  [() => store.items, openBookId],
  () => {
    const done = store.items.find(
      (item) => item.job.book_id === openBookId.value && isAiJobSettled(item.job),
    );
    if (done) store.dismiss(done.job.book_id);
  },
  { deep: true, immediate: true },
);

const settled = (item: ActiveAiTest) => isAiJobSettled(item.job);
const failed = (item: ActiveAiTest) => isAiJobFailed(item.job, item.refresh_error);

function statusLabel(item: ActiveAiTest): string {
  const key = `aiTests.status.${aiJobStatusKey(item.job)}`;
  return te(key) ? t(key) : item.job.status;
}

/** Jarayon ketayotganda — bosqich, bo'lmasa holat */
function detail(item: ActiveAiTest): string {
  const key = `aiTests.phase.${item.job.phase}`;
  return !settled(item) && item.job.phase && te(key) ? t(key) : statusLabel(item);
}

onMounted(() => store.start());
onBeforeUnmount(() => store.stop());
</script>

<template>
  <aside v-if="visible.length" class="ai-tray" :aria-label="t('aiTray.title')">
    <header class="ai-tray__head">
      <AppIcon name="spark" :size="20" class="ai-tray__mark" />
      <b class="flex-1">{{ t('aiTray.title') }}</b>
      <span v-if="runningCount" class="badge badge--info tabular-nums">{{
        t('aiTray.running', { n: runningCount })
      }}</span>
      <button
        type="button"
        class="ai-tray__toggle"
        :aria-expanded="!collapsed"
        :aria-label="collapsed ? t('aiTray.expand') : t('aiTray.collapse')"
        @click="collapsed = !collapsed"
      >
        <AppIcon name="down" :size="20" :class="{ 'rotate-180': collapsed }" />
      </button>
    </header>

    <ul v-show="!collapsed" class="ai-tray__list" role="status" aria-live="polite">
      <li v-for="item in visible" :key="item.job.book_id" class="ai-tray__item">
        <div class="flex items-start gap-2">
          <RouterLink
            class="ai-tray__book"
            :to="{ name: 'admin-questions', params: { id: item.job.book_id } }"
            >{{ item.book_title }}</RouterLink
          >
          <button
            v-if="settled(item)"
            type="button"
            class="ai-tray__close"
            :aria-label="t('aiTray.dismiss')"
            @click="store.dismiss(item.job.book_id)"
          >
            <AppIcon name="close" :size="18" />
          </button>
        </div>

        <template v-if="!settled(item)">
          <p class="text-ink-2">{{ detail(item) }}</p>
          <div v-if="item.job.progress !== null" class="flex items-center gap-2">
            <span
              class="bar bar--live flex-1"
              role="progressbar"
              :aria-valuenow="item.job.progress"
              aria-valuemin="0"
              aria-valuemax="100"
              :aria-label="item.book_title"
              ><i :style="{ width: `${Math.max(item.job.progress, MIN_BAR)}%` }"
            /></span>
            <b class="tabular-nums">{{ item.job.progress }}%</b>
          </div>
        </template>
        <span
          v-else-if="isAiJobReceived(item.job)"
          class="badge self-start"
          :class="aiJobTone('received')"
          >{{ t('aiTests.received', { n: item.job.saved_count ?? 0 }) }}</span
        >

        <p v-if="failed(item)" class="ai-tray__error">
          <AppIcon name="alert" :size="18" />
          {{ t('aiTests.processError') }}
        </p>
      </li>
    </ul>
  </aside>
</template>

<style scoped>
/* Pastki o'ng burchak; dialoglar (Naive) undan yuqorida */
.ai-tray {
  position: fixed;
  right: var(--gutter);
  bottom: var(--gutter);
  z-index: 50;
  width: min(var(--width-xxs), calc(100vw - 2 * var(--gutter)));
  max-height: calc(100vh - 2 * var(--gutter));
  display: flex;
  flex-direction: column;
  border: var(--hair) solid var(--line);
  border-radius: var(--r-md);
  background: var(--surface);
  box-shadow: var(--sh-4);
  overflow: hidden;
}
.ai-tray__head {
  display: flex;
  align-items: center;
  gap: var(--s-tight);
  padding: var(--s-tight) var(--s-min);
  border-bottom: var(--hair) solid var(--line);
}
.ai-tray__mark {
  color: var(--brand-500);
}
.ai-tray__toggle,
.ai-tray__close {
  display: grid;
  place-items: center;
  flex: 0 0 auto;
  padding: var(--spacing-xs);
  border-radius: var(--r-sm);
  color: var(--ink-3);
  cursor: pointer;
}
.ai-tray__toggle:hover,
.ai-tray__close:hover {
  background: var(--surface-2);
  color: var(--ink);
}
.ai-tray__toggle svg {
  transition: transform var(--dur) var(--ease);
}
.ai-tray__list {
  overflow-y: auto;
  overscroll-behavior: contain;
}
.ai-tray__item {
  display: flex;
  flex-direction: column;
  gap: var(--s-icon);
  padding: var(--s-tight) var(--s-min);
}
.ai-tray__item + .ai-tray__item {
  border-top: var(--hair) solid var(--line);
}
.ai-tray__book {
  flex: 1;
  min-width: 0;
  font-weight: var(--fw-semibold);
  overflow-wrap: anywhere;
}
.ai-tray__book:hover {
  color: var(--brand-600);
}
.ai-tray__error {
  display: flex;
  align-items: center;
  gap: var(--s-icon);
  color: var(--danger);
}
</style>
